import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Phone, Loader2, Save, Eye, X, Pencil, Trash2 } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

const CallNoteModal = ({ 
  isOpen, 
  onClose, 
  customer,
  editCall = null, // Pass existing call to edit
  onSaveSuccess,
  onDeleteSuccess,
  showViewOption = false,
  onViewInCallsLog
}) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isEditMode = !!editCall;

  // Initialize notes when editing
  useEffect(() => {
    if (editCall) {
      setNotes(editCall.notes || '');
    } else {
      setNotes('');
    }
  }, [editCall, isOpen]);

  const handleSave = async (andView = false) => {
    if (!notes.trim()) {
      toast.error('Please enter some notes');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        // Update existing call
        const response = await api.put(`${API}/customer-calls/${editCall.id}`, {
          notes: notes.trim()
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data.success) {
          toast.success('Call note updated');
          onClose();
          if (onSaveSuccess) {
            onSaveSuccess({ ...editCall, notes: notes.trim() });
          }
        }
      } else {
        // Create new call
        const response = await api.post(`${API}/customer-calls`, {
          customer_id: customer.id,
          customer_name: customer.ledger_name || customer.account_name || customer.name,
          notes: notes.trim(),
          business_unit: customer.business_unit || customer.customer_type || null
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data.success) {
          toast.success('Call note saved');
          setNotes('');
          
          if (andView && onViewInCallsLog) {
            onViewInCallsLog(customer);
          } else {
            onClose();
          }
          
          if (onSaveSuccess) {
            onSaveSuccess(response.data.call);
          }
        }
      }
    } catch (error) {
      console.error('Error saving call note:', error);
      toast.error(error.response?.data?.detail || 'Failed to save call note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editCall) return;
    
    setDeleting(true);
    try {
      const response = await api.delete(`${API}/customer-calls/${editCall.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        toast.success('Call deleted');
        setShowDeleteConfirm(false);
        onClose();
        if (onDeleteSuccess) {
          onDeleteSuccess(editCall.id);
        }
      }
    } catch (error) {
      console.error('Error deleting call:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete call');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setNotes('');
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="sm:max-w-md"
        style={{ 
          backgroundColor: 'white',
          border: `3px solid ${enconOrange}`,
          borderRadius: '16px'
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${enconOrange}15` }}
            >
              {isEditMode ? (
                <Pencil className="w-5 h-5" style={{ color: enconOrange }} />
              ) : (
                <Phone className="w-5 h-5" style={{ color: enconOrange }} />
              )}
            </div>
            <div>
              <span className="text-lg font-bold" style={{ color: enconOrange }}>
                {isEditMode ? 'Edit Call' : 'Log Call'}
              </span>
              <p className="text-sm font-normal text-gray-500 mt-0.5">
                {editCall?.customer_name || customer?.ledger_name || customer?.account_name || customer?.name || 'Customer'}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {showDeleteConfirm ? (
          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
              <Trash2 className="w-10 h-10 mx-auto text-red-500 mb-2" />
              <p className="font-semibold text-red-700">Delete this call note?</p>
              <p className="text-sm text-red-600 mt-1">This action cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="flex-1"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Notes Input */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Call Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter details about the call..."
                className="min-h-[150px] resize-none"
                style={{ 
                  borderColor: enconOrange,
                  borderRadius: '12px'
                }}
                autoFocus
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              {isEditMode ? (
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSave(false)}
                    disabled={saving || !notes.trim()}
                    className="flex-1 text-white font-semibold"
                    style={{ backgroundColor: enconOrange }}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Update
                  </Button>
                </div>
              ) : showViewOption ? (
                <>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSave(true)}
                      disabled={saving || !notes.trim()}
                      className="flex-1 text-white font-semibold"
                      style={{ backgroundColor: enconOrange }}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Save & View in Calls Log
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSave(false)}
                      disabled={saving || !notes.trim()}
                      variant="outline"
                      className="flex-1 font-semibold"
                      style={{ borderColor: enconOrange, color: enconOrange }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save & Close
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="ghost"
                      className="px-4"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSave(false)}
                    disabled={saving || !notes.trim()}
                    className="flex-1 text-white font-semibold"
                    style={{ backgroundColor: enconOrange }}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Call Note
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CallNoteModal;
